import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Stethoscope, AlertTriangle } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function DoctorRounds() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const load = () => api.get("/patients", { params: { status: "admitted" } }).then(({ data }) => setRows(data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const r = q.toLowerCase();
    return rows.filter(p =>
      (p.name || "").toLowerCase().includes(r) ||
      (p.hospital_id || "").toLowerCase().includes(r) ||
      (p.diagnosis || "").toLowerCase().includes(r) ||
      (p.consultant || "").toLowerCase().includes(r)
    );
  }, [rows, q]);

  const critical = filtered.filter(p => p.critical);
  const rest = filtered.filter(p => !p.critical);

  return (
    <div className="space-y-6" data-testid="doctor-rounds">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Doctor · Daily rounds</div>
          <h1 className="font-display text-3xl font-semibold mt-1">Today's patients</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} admitted · {critical.length} critical</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name / diagnosis / consultant" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-full md:w-80" />
        </div>
      </div>

      {critical.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-red-500 mb-2 flex items-center gap-2"><AlertTriangle size={12} /> Critical babies</div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {critical.map(p => <PatientCard key={p.id} p={p} />)}
          </div>
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Round list</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rest.map(p => <PatientCard key={p.id} p={p} />)}
          {filtered.length === 0 && <div className="col-span-full text-sm text-muted-foreground text-center py-12">No admitted patients.</div>}
        </div>
      </div>
    </div>
  );
}

function PatientCard({ p }) {
  return (
    <Link to={`/patients/${p.id}`} className="card-clinical p-5 hover:border-primary transition-colors" data-testid={`round-${p.hospital_id}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="font-medium truncate">{p.name}</div>
          <div className="text-xs text-muted-foreground font-mono">{p.hospital_id} · Bed {p.bed_number || "—"}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {p.critical && <Badge variant="destructive" className="rounded-full text-[10px]">CRITICAL</Badge>}
          {p.respiratory_support && p.respiratory_support !== "none" && <Badge className="rounded-full text-[10px] uppercase">{p.respiratory_support}</Badge>}
          {p.phototherapy && <Badge className="rounded-full text-[10px] bg-purple-500 hover:bg-purple-500">PHOTO</Badge>}
        </div>
      </div>
      <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.diagnosis || "—"}</div>
      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground tabular">
        <Stethoscope size={12} /> {p.consultant || "—"}
        <span className="ml-auto">DOL {p.day_of_life ?? "—"} · Wt {p.current_weight_g || p.birth_weight_g || "—"}g</span>
      </div>
    </Link>
  );
}
